import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "antd";

import FileViewer from "./FileViewer";
import { isPreviewable } from "./fileTypes";

export { default as FileViewer } from "./FileViewer";
export * from "./fileTypes";

// Set by the mounted provider. Kept module-level so call sites can open a
// preview imperatively from event handlers without prop drilling or hooks.
let openHandler = null;

const openInNewTab = (url) => {
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Shows `url` in the in-app viewer.
 *
 * Falls back to opening a new tab when the file is not something a browser can
 * render (spreadsheets, archives) or when no provider is mounted, so replacing
 * a `window.open` call with this is always at least as good as before.
 */
export const openFilePreview = (url, options = {}) => {
  if (!url) return;
  if (openHandler && isPreviewable(url, options.kind)) {
    openHandler({ url, ...options });
    return;
  }
  openInNewTab(url);
};

export const useFilePreview = () => openFilePreview;

export const FilePreviewProvider = ({ children }) => {
  const [file, setFile] = useState(null);

  useEffect(() => {
    openHandler = setFile;
    return () => {
      openHandler = null;
    };
  }, []);

  const handleClose = useCallback(() => {
    setFile((current) => {
      current?.onClose?.();
      return null;
    });
  }, []);

  return (
    <>
      {children}
      <Modal
        visible={Boolean(file)}
        title={file?.title || "Document"}
        onCancel={handleClose}
        footer={null}
        width="90%"
        style={{ top: 24, maxWidth: 1100 }}
        destroyOnClose
      >
        {file ? (
          <FileViewer
            url={file.url}
            kind={file.kind}
            title={file.title || "Document"}
            downloadLabel={file.downloadLabel}
            downloadSuffix={file.downloadSuffix}
            fileName={file.fileName}
          />
        ) : null}
      </Modal>
    </>
  );
};

export default FilePreviewProvider;
